// WhooLive Web Portal — Data Service Layer (LIVE + partial mock)
//
// Connected to the existing WhooLive backend (single source of truth) via
// REACT_APP_WHOOLIVE_API_URL. CORS is open, so the browser calls it directly.
// JWT Bearer is used exactly as the backend issues it. No secrets in frontend;
// the LiveKit participant token is minted server-side by /streams/{id}/watch.
//
// Still mocked (backend endpoints not yet available): premium events,
// tickets/checkout, replay hub. These are clearly isolated below.

import { ENV } from "@/config/env";
import { myTickets, ownsEvent, bestTypeForEvent } from "@/lib/tickets";
import { mapEvent, mapStream, mapProfile, mapMessage, categoryImage } from "@/lib/mappers";
import { computeEventState } from "@/lib/eventState";
import {
  MOCK_HOME_CATEGORIES,
  MOCK_SPONSORS,
  MOCK_PREMIUM_EVENTS,
  MOCK_STARTING_SOON,
  MOCK_REPLAYS,
  MOCK_PAST_PREMIUM,
  MOCK_FEATURED_LIVE,
  findEvent,
  findLive,
  findUser,
  eventsByHost,
  livesByHost,
} from "@/lib/mockData";

const BASE = ENV.WHOOLIVE_API_URL;

// ---- token + HTTP -----------------------------------------------------------
const TOKEN_KEY = "whoolive_jwt";
let authToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
})();

export const setAuthToken = (t) => {
  authToken = t || null;
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};
export const getAuthToken = () => authToken;

async function fetchOnce(path, { method, headers, body, timeoutMs }) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function http(path, { method = "GET", body, auth = false } = {}) {
  const headers = {};
  // Only set Content-Type when there's a JSON body — avoids a needless CORS
  // preflight (OPTIONS) on simple GET requests.
  if (body) headers["Content-Type"] = "application/json";
  if (auth && authToken) headers.Authorization = `Bearer ${authToken}`;
  const timeoutErr = () => {
    const err = new Error("The request timed out. Please try again.");
    err.code = 408;
    return err;
  };
  const attempt = (ms) => fetchOnce(path, { method, headers, body, timeoutMs: ms });

  // Bounded total deadline (~15s worst case): first try 9s, one GET retry 6s.
  let res;
  try {
    res = await attempt(9000);
  } catch (e) {
    if (e.name !== "AbortError") throw e;
    if (method !== "GET") throw timeoutErr();
    try {
      res = await attempt(6000);
    } catch (e2) {
      if (e2.name === "AbortError") throw timeoutErr();
      throw e2;
    }
  }
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.code = res.status;
    try {
      err.detail = await res.json();
    } catch {
      // Error response had no JSON body — keep the status-based message.
    }
    throw err;
  }
  return res.json();
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
async function mock(resolver) {
  await delay(ENV.MOCK_LATENCY);
  const data = resolver();
  if (data === null || data === undefined) {
    const e = new Error("Not found");
    e.code = 404;
    throw e;
  }
  return data;
}

const buildEventMap = (arr) => {
  const m = {};
  (arr || []).forEach((e) => {
    m[e.id] = mapEvent(e);
  });
  return m;
};

// Build map pins from raw events + live streams (shared by getMapPins & getHome).
const buildMapPins = (eventMap, streams) => {
  const liveByEvent = {};
  (streams || []).forEach((s) => {
    liveByEvent[s.event_id] = s.id;
  });
  return Object.values(eventMap).map((e) => {
    const liveId = liveByEvent[e.id] || null;
    return {
      id: e.id,
      liveId,
      title: e.title,
      location: e.location,
      kind: liveId ? "live" : "event",
      category: e.category,
      viewers: e.attendees,
    };
  });
};

const isReal = (id, prefix) => typeof id === "string" && id.startsWith(prefix);

// Rich map events (real coords + lifecycle state + category color) from raw
// backend payloads. Shared by getMapEvents & getHome. Modular: the map layer
// consumes this shape regardless of the cartographic library used.
const buildMapEvents = (rawEvents, streams) => {
  const liveByEvent = {};
  (streams || []).forEach((s) => {
    liveByEvent[s.event_id] = s.id;
  });
  return (rawEvents || [])
    .filter((e) => typeof e.lat === "number" && typeof e.lng === "number" && !Number.isNaN(e.lat))
    .map((e) => {
      const liveId = liveByEvent[e.id] || null;
      return {
        id: e.id,
        liveId,
        lat: e.lat,
        lng: e.lng,
        title: e.title,
        category: (e.category || "other").toLowerCase(),
        categoryColor: e.category_color || null,
        state: liveId ? "live" : computeEventState(e),
        location: { name: e.location_name || "", city: e.location_name || "" },
        viewers: e.participant_count || e.live_count || 0,
        startsAt: e.starts_at || e.start_time,
        cover: (e.images && e.images[0]) || categoryImage(e.category),
        hostName: e.created_by_name || "Host",
      };
    });
};

export const apiClient = {
  meta: { mode: "live", apiUrl: BASE },

  // ---- Authentication (LIVE) ------------------------------------------------
  auth: {
    login: ({ email, password }) =>
      http("/api/auth/login", { method: "POST", body: { email, password } }).then((r) => {
        setAuthToken(r.token);
        return r;
      }),
    register: ({ email, password, name }) =>
      http("/api/auth/register", { method: "POST", body: { email, password, name } }).then((r) => {
        setAuthToken(r.token);
        return r;
      }),
    me: () => http("/api/auth/me", { auth: true }).then((r) => r.user),
    logout: () => setAuthToken(null),
  },

  // ---- Public events (LIVE) -------------------------------------------------
  getEvents: async ({ category = "All", query = "" } = {}) => {
    const res = await http("/api/events");
    let list = (res.events || []).map(mapEvent);
    if (category && category !== "All") list = list.filter((e) => e.category === category);
    const q = query.trim().toLowerCase();
    if (q)
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.location.city.toLowerCase().includes(q) ||
          e.tags.some((t) => t.toLowerCase().includes(q))
      );
    return list;
  },

  getEvent: (id) =>
    isReal(id, "evt_")
      ? http(`/api/events/${id}`).then((r) => mapEvent(r.event))
      : mock(() => findEvent(id)), // premium/mock events (pe*, e*)

  // ---- Live & upcoming streams (LIVE) ---------------------------------------
  getLiveStreams: async () => {
    const [ev, st] = await Promise.all([http("/api/events"), http("/api/streams/live")]);
    const eventMap = buildEventMap(ev.events);
    return (st.streams || []).map((s) => mapStream(s, eventMap));
  },

  getLive: async (id) => {
    if (!isReal(id, "live_")) return mock(() => findLive(id));
    const [ev, st] = await Promise.all([http("/api/events"), http("/api/streams/live")]);
    const eventMap = buildEventMap(ev.events);
    const found = (st.streams || []).find((s) => s.id === id);
    if (!found) {
      const e = new Error("Stream not found");
      e.code = 404;
      throw e;
    }
    return mapStream(found, eventMap);
  },

  // ---- Map pins (LIVE) ------------------------------------------------------
  getMapPins: async () => {
    const [ev, st] = await Promise.all([http("/api/events"), http("/api/streams/live")]);
    return buildMapPins(buildEventMap(ev.events), st.streams);
  },

  // ---- Rich map events with real coordinates + lifecycle state (LIVE) -------
  getMapEvents: async () => {
    const [ev, st] = await Promise.all([http("/api/events"), http("/api/streams/live")]);
    return buildMapEvents(ev.events, st.streams);
  },

  // ---- User profiles (LIVE) -------------------------------------------------
  getUser: (username) =>
    isReal(username, "user_")
      ? http(`/api/users/${username}`).then((r) => mapProfile(r.user))
      : mock(() => findUser(username)),

  getUserContent: (userId) =>
    isReal(userId, "user_")
      ? http(`/api/users/${userId}`).then((r) => ({
          events: (r.events || []).map(mapEvent),
          lives: [],
        }))
      : mock(() => ({ events: eventsByHost(userId), lives: livesByHost(userId) })),

  // ---- Event chat (LIVE) ----------------------------------------------------
  getEventMessages: (eventId) =>
    http(`/api/events/${eventId}/messages`).then((r) => (r.messages || []).map(mapMessage)),

  postEventMessage: (eventId, text) =>
    http(`/api/events/${eventId}/messages`, { method: "POST", auth: true, body: { text } }).then(
      (r) => mapMessage(r.message)
    ),

  // ---- Live access / LiveKit (LIVE) -----------------------------------------
  // Returns { stream, livekit: { server_url, token } } — token minted server-side.
  watchStream: (streamId) =>
    http(`/api/streams/${streamId}/watch`, { method: "POST", auth: true }),

  startLive: (eventId) =>
    http(`/api/events/${eventId}/live/start`, { method: "POST", auth: true }),

  getLiveKitStatus: () => http("/api/livekit/status"),

  // ---- Homepage aggregate (LIVE reads + mock premium sections) --------------
  getHome: async () => {
    const [ev, st] = await Promise.all([http("/api/events"), http("/api/streams/live")]);
    const eventMap = buildEventMap(ev.events);
    const events = Object.values(eventMap);
    const streams = (st.streams || []).map((s) => mapStream(s, eventMap));

    // Raw events keyed by id — needed for the admin-promotion flag + coordinates
    // that drive the Featured Live prioritization (admin > nearby > most viewed).
    const rawEventById = {};
    (ev.events || []).forEach((e) => {
      rawEventById[e.id] = e;
    });
    const now = Date.now();

    const buildFeatured = (s) => {
      const raw = rawEventById[s.eventId] || {};
      const promoted = !!(
        raw.promotion_expires_at && new Date(raw.promotion_expires_at).getTime() > now
      );
      return {
        id: s.id,
        title: s.title,
        subtitle: `Live from ${s.location.city || "the WhooLive map"}`,
        hero: s.thumbnail,
        category: s.category,
        viewers: s.viewers,
        host: s.host,
        location: s.location,
        description:
          (eventMap[s.eventId] && eventMap[s.eventId].description) ||
          "Streaming live now on WhooLive.",
        tagline: promoted ? "Promoted live" : "Featured live now",
        // Prioritization metadata (consumed by Home.jsx):
        promoted,
        lat: typeof raw.lat === "number" ? raw.lat : s.location.lat ?? null,
        lng: typeof raw.lng === "number" ? raw.lng : s.location.lng ?? null,
      };
    };

    // All live streams as featured candidates, ordered by the default priority:
    // 1) admin-promoted (most viewed among promoted), else 2) most viewed overall.
    const featuredCandidates = streams.map(buildFeatured);
    const pickDefault = (list) => {
      if (!list.length) return MOCK_FEATURED_LIVE;
      const promoted = list.filter((c) => c.promoted).sort((a, b) => b.viewers - a.viewers);
      if (promoted.length) return promoted[0];
      return [...list].sort((a, b) => b.viewers - a.viewers)[0];
    };
    const featured = pickDefault(featuredCandidates);

    const nearby = events.slice(0, 6).map((e) => ({
      id: e.id,
      title: e.title,
      thumbnail: e.cover,
      city: e.location.city,
      status: e.isLive ? "live" : "upcoming",
      watching: e.attendees,
      time: new Date(e.startsAt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    }));

    const popular = streams.slice(0, 8).map((s) => ({
      id: s.id,
      liveId: s.id,
      title: s.title,
      thumbnail: s.thumbnail,
      viewers: s.viewers,
      views: s.viewers,
      city: s.location.city,
    }));

    return {
      featured,
      featuredCandidates,
      nearby,
      popular,
      mapEvents: buildMapEvents(ev.events, st.streams),
      mapPins: buildMapPins(eventMap, st.streams),
      categories: MOCK_HOME_CATEGORIES,
      sponsors: MOCK_SPONSORS,
      premiumEvents: MOCK_PREMIUM_EVENTS, // MOCKED — no backend endpoint yet
      startingSoon: MOCK_STARTING_SOON, // MOCKED — no backend endpoint yet
    };
  },

  // ---- Still MOCKED (no backend endpoint available yet) ---------------------
  getPremiumEvents: () => mock(() => MOCK_PREMIUM_EVENTS),
  getMyTickets: () => mock(() => myTickets()),
  getReplayHub: () =>
    mock(() => {
      const premium = MOCK_PAST_PREMIUM.filter((e) => e.replay).map((event) => {
        const owned = ownsEvent(event.id) && bestTypeForEvent(event.id) === "online";
        const end = new Date(event.endsAt).getTime();
        const expiresAt = new Date(end + event.replay.durationHours * 3600 * 1000).toISOString();
        const available = owned && Date.now() <= new Date(expiresAt).getTime();
        return { event, owned, available, expired: owned && !available, expiresAt, locked: !owned };
      });
      return { free: MOCK_REPLAYS, premium };
    }),
};

export default apiClient;
